import type { QueryClient } from '@tanstack/react-query';
import { REWARDS_HUB_QK } from './queryKeys';

/**
 * Keeping the hub's "ready to claim" counter honest when a reward becomes
 * claimable outside the hub (24.09).
 *
 * Steps and tiers close on the backend, asynchronously: an order, a fill or a
 * deposit goes to Kafka, gamification closes the onboarding step, and a tier
 * waits for the ladder sweep (every 20 s). No hub mutation is involved, so
 * nothing invalidated the cache and the header counter stayed stale.
 */

/**
 * Inbox types that mean a reward is waiting or a rank moved. notification-
 * service writes an inbox row per hub event, `type` = the event, and the row
 * over INBOX_NEW is the only push the web gets for them.
 */
const REFRESHING_INBOX_TYPES: ReadonlySet<string> = new Set(['REWARDS_READY', 'RANK_UP']);

export function isRewardsHubRefreshSignal(inboxType: string): boolean {
  return REFRESHING_INBOX_TYPES.has(inboxType);
}

/**
 * Refresh what a hub event can move. Only the queries on screen refetch; the
 * rest are marked stale and refetch when next shown.
 */
export function invalidateRewardsHubState(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: REWARDS_HUB_QK.counters });
  queryClient.invalidateQueries({ queryKey: REWARDS_HUB_QK.profile });
  queryClient.invalidateQueries({ queryKey: REWARDS_HUB_QK.ladders });
  queryClient.invalidateQueries({ queryKey: REWARDS_HUB_QK.badges });
  queryClient.invalidateQueries({ queryKey: REWARDS_HUB_QK.season });
}

/**
 * When to re-read the counter after the player's own trading or a deposit.
 * At once is too early — the backend has not consumed the event yet and the
 * refetch brings the old number back; ~3 s covers an onboarding step, ~25 s
 * the tier that closes on the 20-second ladder sweep.
 */
const COUNTER_REFRESH_DELAYS_MS = [3_000, 25_000] as const;

/**
 * The backstop for the inbox signal: `REWARDS_READY` is capped at one a day
 * and a player can turn hub notifications off, so their own actions refresh
 * the counter too. Only `/counters` — it is four COUNTs; the profile is heavy.
 */
export function scheduleRewardsCountersRefresh(queryClient: QueryClient): void {
  for (const delay of COUNTER_REFRESH_DELAYS_MS) {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: REWARDS_HUB_QK.counters });
    }, delay);
  }
}
